// apps/provider/src/services/aiService.js

import { apiService } from './api';
import { authService } from './auth';
class AIService {
  constructor() {
    this.abortControllers = new Map();
  }

  // Transcribe audio using streaming API
  async transcribeAudio(audioBlob, options = {}) {
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    };
  
    const base64String = await fileToBase64(audioBlob);
    
    // Send the full data URL - Xano will create a file resource from it
    const response = await apiService.request('/ai/transcribe', {
      method: 'POST',
      timeoutMs: 300000,
      body: JSON.stringify({
        audio: base64String, // Full "data:audio/webm;base64,xxxxx" string
        filename: `recording_${Date.now()}.webm`,
        model: 'whisper-1',
        language: options.language || 'en'
      })
    });
  
    return response;
  }

  // Convert transcript to SOAP note
  async generateSOAPNote(transcript, patientContext = {}, options = {}) {
    const prompt = this.buildSOAPPrompt(transcript, patientContext);
    
    try {
      const token = authService.getToken();
      const response = await fetch(`${apiService.baseUrl}/ai/generate-soap`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          prompt,
          model: 'gpt-4-turbo-preview',
          temperature: 0.3,
          max_tokens: 2000
        })
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let contentBuffer = '';
      
      // Initialize the result structure
      const result = {
        subjective: { content: '' },
        objective: { content: '' },
        assessment: { content: '' },
        plan: { content: '' },
        icd10_suggestions: []
      };
      
      let currentSection = null;
  
      const processContentLine = (line) => {
        
        // Check for section markers
        if (line.startsWith('SECTION|')) {
          const section = line.split('|')[1].toLowerCase();
          currentSection = section;
        }
        // Check for ICD10 codes
        else if (line.startsWith('ICD10|')) {
          const parts = line.split('|');
          result.icd10_suggestions.push({
            code: parts[1],
            description: parts[2],
            is_primary: parts[3] === 'true'
          });
        }
        // Regular content line
        else if (currentSection && line.trim()) {
          result[currentSection].content += line + '\n';
          
          // Call progress callback if provided
          if (options.onProgress) {
            options.onProgress({
              section: currentSection,
              content: line,
              partial: result
            });
          }
        }
      };
  
      // Read the stream
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete SSE messages
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';
        
        for (const message of messages) {
          const lines = message.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.substring(6);
              
              if (data === '[DONE]') {
                // Trim all content sections
                Object.keys(result).forEach(key => {
                  if (result[key].content !== undefined) {
                    result[key].content = result[key].content.trim();
                  }
                });
                return result;
              }
              
              try {
                const jsonData = JSON.parse(data);
                
                if (jsonData.choices?.[0]?.delta?.content) {
                  contentBuffer += jsonData.choices[0].delta.content;
                  
                  // Process complete lines
                  if (contentBuffer.includes('\n')) {
                    const contentLines = contentBuffer.split('\n');
                    contentBuffer = contentLines.pop() || '';
                    
                    contentLines.forEach(contentLine => {
                      if (contentLine.trim()) {
                        processContentLine(contentLine.trim());
                      }
                    });
                  }
                }
              } catch (error) {
                console.error('Failed to parse SOAP JSON:', error);
              }
            }
          }
        }
      }
      
      // Process any remaining content
      if (contentBuffer.trim()) {
        processContentLine(contentBuffer.trim());
      }
      
      return result;
      
    } catch (error) {
      console.error('SOAP generation error:', error);
      throw error;
    }
  }

  // Build SOAP prompt from transcript
  buildSOAPPrompt(transcript, context) {
    return `
      You are a medical scribe. Convert the following patient-provider conversation into a SOAP note.
      
      Patient Information:
      - Name: ${context.patientName || 'N/A'}
      - DOB: ${context.patientDOB || 'N/A'}
      - Chief Complaint: ${context.chiefComplaint || 'N/A'}
      
      Transcript:
      ${transcript}
      
      Instructions for response format:
      - Start with SECTION|SUBJECTIVE
      - Output subjective findings line by line
      - Next output SECTION|OBJECTIVE
      - Output objective findings line by line
      - Next output SECTION|ASSESSMENT
      - Output assessment points line by line
      - Next output SECTION|PLAN
      - Output plan items line by line
      - For each ICD-10 code: ICD10|{code}|{description}|{true/false for primary}
      
      Format each section with:
      - Clear, concise medical language
      - Numbered items where appropriate
      - Standard medical abbreviations
      
      DO NOT include any JSON formatting or brackets.
      Output ONLY the section markers and content as specified.
    `;
  }

  // Parse SOAP response
  parseSOAPResponse(response) {
    try {
      // Extract JSON from the response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      
      // Fallback: parse as structured text
      return {
        subjective: { content: this.extractSection(response, 'SUBJECTIVE') },
        objective: { content: this.extractSection(response, 'OBJECTIVE') },
        assessment: { content: this.extractSection(response, 'ASSESSMENT') },
        plan: { content: this.extractSection(response, 'PLAN') },
        icd10_suggestions: []
      };
    } catch (error) {
      console.error('Failed to parse SOAP response:', error);
      return null;
    }
  }

  // Extract section from text
  extractSection(text, sectionName) {
    const regex = new RegExp(`${sectionName}:?\\s*([\\s\\S]*?)(?=(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN|$))`, 'i');
    const match = text.match(regex);
    return match ? match[1].trim() : '';
  }

  // Get AI copilot suggestions
  async getCopilotSuggestion(context, query) {
    const token = authService.getToken();
    const response = await fetch(`${apiService.baseUrl}/ai/copilot`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify({
        context,
        query,
        model: 'gpt-4-turbo-preview',
        temperature: 0.5,
        max_tokens: 500
      })
    });
  
    // Return the response for the store to handle streaming
    return response;
  }

  // Cancel request
  cancelRequest(requestId) {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
    }
  }

  // Get ICD-10 suggestions
  async getICDSuggestions(symptoms, diagnoses) {
    try {
      const response = await apiService.request('/ai/icd-suggestions', {
        method: 'POST',
        body: JSON.stringify({ symptoms, diagnoses })
      });
      
      return response.suggestions || [];
    } catch (error) {
      console.error('Failed to get ICD suggestions:', error);
      return [];
    }
  }

  // Get medication recommendations
  async getMedicationRecommendations(diagnosis, patientInfo) {
    try {
      const response = await apiService.request('/ai/medication-recommendations', {
        method: 'POST',
        body: JSON.stringify({ diagnosis, patientInfo })
      });
      
      return response.recommendations || [];
    } catch (error) {
      console.error('Failed to get medication recommendations:', error);
      return [];
    }
  }
}

export const aiService = new AIService();